/**
 * Tax Calculator utilities.
 *
 * Automatically calculates tax based on:
 *   - Provider country/state (where service is delivered)
 *   - Buyer country (for VAT purposes)
 *   - Tax rates configured in TAX_RATES_JSON env var
 *
 * Tax rate config format (TAX_RATES_JSON):
 * {
 *   "FI": { "rate": 0.24, "label": "VAT 24%" },
 *   "DE": { "rate": 0.19, "label": "MwSt 19%" },
 *   "default": { "rate": 0.20, "label": "VAT 20%" }
 * }
 *
 * Listing can override: listing.publicData.taxRate (number 0-1)
 */

export const DEFAULT_TAX_RATE = 0.20;
export const TAX_INCLUDED_LABEL = 'incl. tax';

/**
 * Get tax rate for a country code.
 * @param {string} countryCode — ISO 3166-1 alpha-2
 * @param {Object} [rates] — custom rates map
 * @returns {{ rate: number, label: string }}
 */
export const getTaxRate = (countryCode, rates) => {
  const rateMap = rates || (() => {
    try { return JSON.parse(process.env.TAX_RATES_JSON || '{}'); }
    catch (_) { return {}; }
  })();

  const config = rateMap[countryCode] || rateMap['default'];
  if (!config) return { rate: DEFAULT_TAX_RATE, label: `VAT ${Math.round(DEFAULT_TAX_RATE * 100)}%` };
  return config;
};

/**
 * Calculate tax amount (tax-exclusive price → tax amount).
 * @param {number} netCents — price before tax (cents)
 * @param {number} rate — tax rate (0-1)
 * @returns {{ taxCents: number, grossCents: number }}
 */
export const calculateTax = (netCents, rate) => {
  const taxCents = Math.round(netCents * rate);
  return { taxCents, grossCents: netCents + taxCents };
};

/**
 * Extract tax from gross price (tax-inclusive).
 * @param {number} grossCents — price including tax
 * @param {number} rate
 * @returns {{ taxCents: number, netCents: number }}
 */
export const extractTax = (grossCents, rate) => {
  const netCents = Math.round(grossCents / (1 + rate));
  const taxCents = grossCents - netCents;
  return { taxCents, netCents };
};

/**
 * Get custom tax rate from listing publicData.
 * @param {Object|null} listing
 * @returns {number|null} — rate (0-1) or null
 */
export const getListingTaxRate = (listing) => {
  const rate = listing?.attributes?.publicData?.taxRate;
  if (rate === null || rate === undefined) return null;
  const n = parseFloat(rate);
  if (isNaN(n) || n < 0 || n > 1) return null;
  return n;
};
